import { SimConstants } from "@highmarch/domain";
import { TickPacer } from "./tickPacer";

/**
 * Turns real frame time into whole 20 Hz sim ticks for the level loop: the game speed multiplies the time fed in, a
 * pause feeds none, and a slow frame drops ticks rather than piling them up (a backlog the next frame cannot clear
 * either is a death spiral: each frame spends longer ticking, so the next arrives later and owes even more).
 *
 * Pure bookkeeping, no clock of its own: the loop passes each frame's seconds, so it is unit-tested without a browser.
 */
export class SimClock {
  static readonly TickSeconds = 1 / SimConstants.TicksPerSecond;
  /**
   * Ticks one frame may run. ×3 at a 30 fps frame rate is 60 ticks a second, two a frame; eight leaves room for a
   * hitch (a 0.25 s frame at ×3 owes 15 and runs 8) without one slow frame stalling the next for a whole sim second.
   */
  static readonly MaxTicksPerFrame = 8;
  /** Longest frame counted (s): after a stall (a backgrounded tab, a debugger) the level resumes, it does not catch up. */
  static readonly MaxFrameSeconds = 0.25;

  /** Game-speed multiplier on sim time (the HUD's ×1 / ×2 / ×3). */
  speed = 1;
  /** Holds back ticks that would make an on-time frame late (tickPacer.ts); used when `take` is given the frame time. */
  readonly pacer = new TickPacer();
  private acc = 0;
  private held = false;

  get paused(): boolean { return this.held; }

  /** Pausing stops the sim dead; resuming starts from the fraction of a tick already banked, never from a backlog. */
  setPaused(on: boolean): void {
    this.held = on;
    this.pacer.reset();
    if (!on) this.acc = Math.min(this.acc, SimClock.TickSeconds * SimClock.MaxResumeCarry);
  }

  /**
   * Feeds a frame's sim seconds (real time already scaled by hit-stop) and returns how many ticks to run now. While
   * paused nothing is banked, so resuming never releases a burst of ticks. `frameSeconds`, the frame's real interval,
   * turns on the pacer (the level loop passes it, and reports each frame's costs to `pacer.observe`): ticks that would
   * keep the frame rate stuck below the display's are dropped like the ones past the cap.
   */
  take(simSeconds: number, frameSeconds?: number): number {
    if (this.held) return 0;
    const sim = Math.max(0, simSeconds);
    this.acc += sim * this.speed;
    const due = SimClock.wholeTicks(this.acc);
    let run = Math.min(due, SimClock.MaxTicksPerFrame);
    if (frameSeconds !== undefined && frameSeconds > 0) {
      run = this.pacer.limit(run, frameSeconds, (sim / frameSeconds) * this.speed * SimConstants.TicksPerSecond);
    }
    // Past the cap (or the pacer's limit) the backlog is forgotten; either way the partial tick stays, so interpolation does not jump.
    this.acc = Math.max(0, this.acc - due * SimClock.TickSeconds);
    return run;
  }

  private static wholeTicks(seconds: number): number { return Math.floor(seconds / SimClock.TickSeconds + SimClock.Epsilon); }

  /** How far into the next tick the banked time is (0..1), for the views' interpolation. */
  get alpha(): number { return Math.min(1, this.acc / SimClock.TickSeconds); }

  /** A fraction of a tick banked at pause is kept on resume (it is what the units are drawn at); more never is. */
  private static readonly MaxResumeCarry = 1;
  /** Float slack: 0.15 s at ×1 is exactly three ticks, not 2.9999999. */
  private static readonly Epsilon = 1e-9;
}
